import {
  type Context,
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
} from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HookType = (...args: any) => any;

type ContextProviderProps<T extends HookType> = {
  Context: Context<ReturnType<T> | undefined>;
  children: ReactElement | ReactNode;
  useStateHook: T;
} & { stateHookProps: Parameters<T>[0] };

function ContextProvider<T extends HookType>({
  Context,
  children,
  useStateHook,
  stateHookProps,
}: ContextProviderProps<T>) {
  const context = useStateHook(stateHookProps);

  return <Context.Provider value={context}>{children}</Context.Provider>;
}

function getUseContext<T extends HookType>(
  Context: Context<ReturnType<HookType> | undefined>,
) {
  return () => {
    const context = useContext<ReturnType<T>>(Context);

    if (context === undefined) {
      throw new Error("useContext must be used within a ContextProvider");
    }

    return context;
  };
}

export function buildContext<T extends HookType>(stateHook: T) {
  const Context = createContext<ReturnType<T> | undefined>(undefined);

  return {
    Context,
    ContextProvider: ({
      children,
      ...stateHookProps
    }: (Parameters<T>[0] extends undefined ? object : Parameters<T>[0]) & {
      children: ReactElement | ReactNode;
    }) => (
      <ContextProvider<T>
        Context={Context}
        stateHookProps={stateHookProps}
        useStateHook={stateHook}
      >
        {children}
      </ContextProvider>
    ),
    useContext: getUseContext<T>(Context),
  };
}
